/**
 * Module-local utility helpers for the online-visa feature.
 */
import { parsePhoneNumber } from 'libphonenumber-js/mobile';

import isValid from 'date-fns/isValid';
import {
  ENTRY_LABELS,
  VISA_PURPOSE_OPTIONS,
  VISA_TYPE_LABELS,
} from '../constants/onlineVisaConstants';

/**
 * Convert the API's `entry` field into a user-friendly label.
 * Examples: "MULTIPLE" -> "Multiple Entry"
 */
export const formatEntry = (entry) => {
  if (!entry) return '';
  return ENTRY_LABELS[entry.toUpperCase()] || `${entry} Entry`;
};

/**
 * Convert the API's `visaType` field into a user-friendly label.
 */
export const formatVisaType = (visaType) => {
  if (!visaType) return '';
  return VISA_TYPE_LABELS[visaType] || visaType;
};

/**
 * Convert the search form's visa purpose value into its dropdown label.
 * The "All Visa Types" option intentionally uses an empty value.
 */
export const formatVisaPurpose = (visaPurpose) => {
  const option = VISA_PURPOSE_OPTIONS.find(
    (item) => item.value === (visaPurpose || ''),
  );
  return option?.label || visaPurpose || '';
};

/**
 * Format a fare number into a localised currency string.
 * Falls back to `priceDisplay` if numeric amount isn't available.
 */
export const formatPrice = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return '';
  }
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch (e) {
    return `${currency} ${Math.round(Number(amount))}`;
  }
};

/**
 * Build a compact "30 DAYS", "10 YEARS" label for duration/validity blocks.
 */
export const formatDurationLabel = (value, type) => {
  if (!value) return '';
  const typeLabel = (type || '').toLowerCase();
  return `${value} ${typeLabel}`.trim();
};

const toAgesCsv = (arr) =>
  Array.isArray(arr) ? arr.filter((v) => v !== '' && v != null).join(',') : '';

const fromAgesCsv = (csv) => {
  if (!csv) return [];
  return String(csv)
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '')
    .map((v) => Number(v));
};

/**
 * Serialize the search form state into a URL-safe query string.
 */
export const buildSearchQuery = (searchState) => {
  const params = new URLSearchParams();
  if (searchState.nationality)
    params.set('nationality', searchState.nationality);
  if (searchState.destinationCode) {
    params.set('destinationCode', searchState.destinationCode);
    params.set('destinationName', searchState.destinationName || '');
  }
  if (searchState.visaPurpose)
    params.set('visaPurpose', searchState.visaPurpose);
  params.set('adults', String(searchState.adults || 1));
  params.set('children', String(searchState.children || 0));
  params.set('infants', String(searchState.infants || 0));
  const childAgesCsv = toAgesCsv(searchState.childAges);
  if (childAgesCsv) params.set('childAges', childAgesCsv);
  const infantAgesCsv = toAgesCsv(searchState.infantAges);
  if (infantAgesCsv) params.set('infantAges', infantAgesCsv);
  return params.toString();
};

/**
 * Parse query params back into the search state used by the search form.
 */
export const parseSearchQuery = (searchParams) => ({
  nationality: searchParams.get('nationality') || 'IN',
  destinationCode: searchParams.get('destinationCode') || '',
  destinationName: searchParams.get('destinationName') || '',
  visaPurpose: searchParams.get('visaPurpose') || '',
  adults: Number(searchParams.get('adults') || 1),
  children: Number(searchParams.get('children') || 0),
  infants: Number(searchParams.get('infants') || 0),
  childAges: fromAgesCsv(searchParams.get('childAges')),
  infantAges: fromAgesCsv(searchParams.get('infantAges')),
});

/**
 * Format a JS Date (or ISO/YYYY-MM-DD string) to the dd-MM-yyyy layout used by
 * the PERFORMVISABOOKING payload. Returns '' if input is empty/invalid.
 */
export const toVisaDateString = (date) => {
  if (!date) return '';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch (e) {
    return '';
  }
};

/**
 * Compute whole-year age (as of today) from a DOB (Date or yyyy-mm-dd string).
 */
export const calculateAge = (dob) => {
  if (!dob) return '';
  try {
    const d = dob instanceof Date ? dob : new Date(dob);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
      age -= 1;
    }
    return age;
  } catch (e) {
    return '';
  }
};

export const isNullOrEmpty = (value) => {
  try {
    if (
      value === '' ||
      value === null ||
      value === 'null' ||
      value === undefined ||
      value === 'undefined' ||
      value === false ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const isValidMobileNumber = (mobileNumber, countryCode = 'IN') => {
  try {
    if (isNullOrEmpty(mobileNumber)) {
      return false;
    }
    const cleaned = mobileNumber.toString().trim();

    if (!/^\d+$/.test(cleaned)) {
      return false;
    }
    // Parse assuming the given country
    const parsed = parsePhoneNumber(cleaned, countryCode);
    // Ensure it's valid AND belongs to the expected country
    return parsed.isValid() && parsed.country === countryCode;
  } catch (error) {
    return false;
  }
};

export const isValidDate = (date) => {
  try {
    if (isValid(date)) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};
